import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { auth, HttpError, type IndividualPath } from '../api/client';
import { Alert, Button, Field, Input, Modal } from '../components/ui';
import BrandLogo from '../components/BrandLogo';

/** Destination RequireAuth saved when it bounced a logged-out visitor to /login. */
export function savedDestination(state: unknown): string | null {
  const from = (state as { from?: { pathname?: string; search?: string } } | null)?.from;
  if (typeof from?.pathname === 'string' && from.pathname.startsWith('/app')) {
    return from.pathname + (from.search || '');
  }
  return null;
}

type Tab = 'login' | 'signup';
type WorkspaceKind = 'ORGANIZATION' | 'INDIVIDUAL';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INDIVIDUAL_PATHS: { value: IndividualPath; title: string; desc: string }[] = [
  { value: 'MYSELF', title: 'Myself', desc: 'Scholarships, grants & fellowships for you' },
  { value: 'MY_CHILD', title: 'My child', desc: 'K–12 programs & college aid for your child' },
  { value: 'MY_STUDENT', title: 'My student', desc: 'Aid for a student you mentor or teach' },
  { value: 'LIVING_EXPENSES', title: 'Help with bills & living expenses', desc: 'Benefits and local aid for rent, utilities, food & more' },
];

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.87c2.26-2.09 3.57-5.16 3.57-8.81z" />
      <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.94-2.91l-3.87-3c-1.07.72-2.45 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.29v3.1A12 12 0 0 0 12 24z" />
      <path fill="#FBBC05" d="M5.27 14.28A7.2 7.2 0 0 1 4.89 12c0-.79.14-1.56.38-2.28v-3.1H1.29a12 12 0 0 0 0 10.76l3.98-3.1z" />
      <path fill="#EA4335" d="M12 4.76c1.76 0 3.34.61 4.58 1.8l3.44-3.44A11.98 11.98 0 0 0 12 0 12 12 0 0 0 1.29 6.62l3.98 3.1C6.22 6.87 8.87 4.76 12 4.76z" />
    </svg>
  );
}

/** Forgot-password flow: emails a one-hour reset link when the mailer is
    configured; otherwise falls back to the honest support-assisted path. */
function ForgotPasswordModal({ initialEmail, onClose }: { initialEmail: string; onClose: () => void }) {
  const [resetEmail, setResetEmail] = useState(initialEmail);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [result, setResult] = useState<'sent' | 'support' | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = resetEmail.trim();
    if (!value) {
      setEmailError('Email is required.');
      return;
    }
    if (!EMAIL_RE.test(value)) {
      setEmailError('Enter a valid email address.');
      return;
    }
    setEmailError(null);
    setBusy(true);
    setServerError(null);
    try {
      const res = await auth.forgotPassword(value);
      setResult(res.mailerConfigured ? 'sent' : 'support');
    } catch (err) {
      setServerError(err instanceof HttpError ? err.message : 'Could not reach the server. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Reset your password" onClose={onClose}>
      {result === 'sent' ? (
        <>
          <Alert tone="success">
            Check your email for a reset link (expires in 1 hour).
          </Alert>
          <p>
            If an account uses <strong>{resetEmail.trim()}</strong>, a reset link is on
            its way. The link expires in 1 hour — if it lapses, just request a new one.
          </p>
          <div className="form-actions">
            <Button variant="primary" onClick={onClose}>
              Done
            </Button>
          </div>
        </>
      ) : result === 'support' ? (
        <>
          <p>
            Email password reset isn&apos;t switched on yet — our support team
            handles resets personally instead. Send us a note from the email
            address on your account and we&apos;ll reset it for you:
          </p>
          <p>
            <a href="mailto:support@grantrfpnavigator.com">support@grantrfpnavigator.com</a>
          </p>
          <div className="form-actions">
            <Button variant="primary" onClick={onClose}>
              Got it
            </Button>
          </div>
        </>
      ) : (
        <form onSubmit={submit} noValidate>
          <p>Enter the email address on your account and we&apos;ll send you a link to set a new password.</p>
          {serverError && <Alert tone="error">{serverError}</Alert>}
          <Field label="Email address" required error={emailError ?? undefined}>
            <Input
              id="reset-email"
              type="email"
              autoComplete="email"
              placeholder="you@organization.org"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              aria-invalid={!!emailError}
            />
          </Field>
          <div className="form-actions">
            <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={busy}>
              {busy ? 'Sending…' : 'Send reset link'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

export function AuthPage({ initialTab }: { initialTab: Tab }) {
  const { login, signup } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>(initialTab);
  const authTabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const AUTH_TABS: Tab[] = ['login', 'signup'];
  const onAuthTabKeyDown = (e: React.KeyboardEvent, index: number) => {
    let next: number | null = null;
    if (e.key === 'ArrowRight') next = (index + 1) % AUTH_TABS.length;
    else if (e.key === 'ArrowLeft') next = (index - 1 + AUTH_TABS.length) % AUTH_TABS.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = AUTH_TABS.length - 1;
    if (next !== null) {
      e.preventDefault();
      setTab(AUTH_TABS[next]);
      window.setTimeout(() => authTabRefs.current[next as number]?.focus(), 0);
    }
  };
  const [oauthReady, setOauthReady] = useState<boolean | null>(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [orgName, setOrgName] = useState('');
  const [suEmail, setSuEmail] = useState('');
  const [suPassword, setSuPassword] = useState('');
  const [workspaceKind, setWorkspaceKind] = useState<WorkspaceKind>(
    // Deep link from marketing (e.g. /signup?kind=individual) pre-selects the workspace kind.
    () => (searchParams.get('kind') === 'individual' ? 'INDIVIDUAL' : 'ORGANIZATION'),
  );
  const [individualPath, setIndividualPath] = useState<IndividualPath | ''>('');

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resetNoteOpen, setResetNoteOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    auth
      .oauthStatus()
      .then((s) => {
        if (!cancelled) setOauthReady(s.google);
      })
      .catch(() => {
        if (!cancelled) setOauthReady(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (searchParams.get('oauth_error')) {
      setServerError('Google sign-in did not complete. Please try again or use email.');
    }
  }, [searchParams]);

  const goApp = (kind?: string) => {
    // Honor the deep link RequireAuth saved (e.g. /app/onboarding?path=nonprofit
    // from a marketing CTA) so visitors land where the button promised.
    const dest = savedDestination(location.state);
    if (dest) {
      navigate(dest, { replace: true });
      return;
    }
    navigate(kind === 'INDIVIDUAL' ? '/app/individual/onboarding' : '/app/overview', { replace: true });
  };

  const fail = (err: unknown, fallback: string) => {
    if (err instanceof HttpError && err.code === 'INVALID_CREDENTIALS') {
      setServerError('Invalid email or password. Please try again.');
    } else if (err instanceof HttpError && err.code === 'EMAIL_TAKEN') {
      setServerError('An account with this email already exists. Try signing in.');
    } else if (err instanceof HttpError) {
      setServerError(err.message);
    } else {
      setServerError(fallback);
    }
  };

  const submitLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!email.trim()) next.email = 'Email is required.';
    if (!password) next.password = 'Password is required.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setBusy(true);
    setServerError(null);
    try {
      const me = await login(email.trim(), password);
      const kind = me.memberships.find((m) => m.organizationId === me.currentOrgId)?.kind;
      goApp(kind);
    } catch (err) {
      fail(err, 'Could not reach the server. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const submitSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = 'Your name is required.';
    if (workspaceKind === 'ORGANIZATION' && !orgName.trim()) next.orgName = 'Organization name is required.';
    if (workspaceKind === 'INDIVIDUAL' && !individualPath) next.individualPath = 'Choose who the funding search is for.';
    if (!suEmail.trim()) next.suEmail = 'Email is required.';
    else if (!EMAIL_RE.test(suEmail.trim())) next.suEmail = 'Enter a valid email address.';
    if (suPassword.length < 8) next.suPassword = 'Password must be at least 8 characters.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setBusy(true);
    setServerError(null);
    try {
      const me = await signup({
        name: name.trim(),
        email: suEmail.trim(),
        password: suPassword,
        kind: workspaceKind,
        ...(workspaceKind === 'ORGANIZATION'
          ? { orgName: orgName.trim() }
          : { individualPath: individualPath as IndividualPath }),
      });
      const kind = me.memberships.find((m) => m.organizationId === me.currentOrgId)?.kind;
      goApp(kind);
    } catch (err) {
      fail(err, 'Could not reach the server. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const googleEnabled = oauthReady === true;

  // New Google users choose their workspace type up front (signup tab);
  // the choice rides through OAuth state so the callback creates the right
  // kind of workspace. Existing users are unaffected.
  // The saved deep-link destination rides along too, so the server callback
  // can land the user where the marketing CTA promised.
  const googleReturnTo = savedDestination(location.state);
  const googleHref =
    tab === 'signup' && workspaceKind === 'INDIVIDUAL' && individualPath
      ? `/api/v1/auth/google?kind=INDIVIDUAL&individualPath=${individualPath}${
          googleReturnTo ? `&returnTo=${encodeURIComponent(googleReturnTo)}` : ''
        }`
      : `/api/v1/auth/google${googleReturnTo ? `?returnTo=${encodeURIComponent(googleReturnTo)}` : ''}`;

  const handleGoogleClick = (e: React.MouseEvent) => {
    if (tab === 'signup' && workspaceKind === 'INDIVIDUAL' && !individualPath) {
      e.preventDefault();
      setErrors((prev) => ({ ...prev, individualPath: 'Choose who the funding search is for first.' }));
      return;
    }
    // Google's server-side callback reloads the app, so the SPA
    // can't distinguish it from a restored session. Leave a
    // same-tab marker so the boot-time refresh can treat it as a
    // fresh login and trigger the profile-nudge check.
    try {
      sessionStorage.setItem('grantnav.oauthFreshLogin', '1');
    } catch {
      /* storage unavailable — nudge simply won't trigger */
    }
  };

  return (
    <div className="auth-wrap">
      {/* Animated visual panel */}
      <div className="auth-visual" aria-hidden="true">
        <span className="auth-orb o1" />
        <span className="auth-orb o2" />
        <span className="auth-orb o3" />
        <span className="auth-orb o4" />
        <div className="auth-brand">
          <BrandLogo variant="white" height={64} />
        </div>
        <div className="auth-visual-copy">
          <h2>From funding search to a winning draft.</h2>
          <p>
            Discover grants and RFPs matched to your organization, understand why they fit,
            and move every submission from research to deadline — with seven AI teammates
            working alongside your team.
          </p>
          <div className="auth-visual-proof">
            <div className="auth-proof-stat"><div className="n">Live</div><div className="l">grants &amp; RFPs tracked</div></div>
            <div className="auth-proof-stat"><div className="n">Fit scores</div><div className="l">on every opportunity</div></div>
            <div className="auth-proof-stat"><div className="n">7 guides</div><div className="l">walking you through</div></div>
          </div>
        </div>
        <div className="text-sm" style={{ color: '#8fa3bd' }}>
          Real data · your workspace · ready in minutes
        </div>
      </div>

      {/* Form side */}
      <main className="auth-form-side">
        <div className="auth-card">
          <div className="auth-tabs" role="tablist" aria-label="Sign in or create account">
            {AUTH_TABS.map((t, i) => (
              <button
                key={t}
                type="button"
                role="tab"
                id={`auth-tab-${t}`}
                aria-selected={tab === t}
                aria-controls="auth-panel"
                tabIndex={tab === t ? 0 : -1}
                ref={(el) => { authTabRefs.current[i] = el; }}
                onClick={() => setTab(t)}
                onKeyDown={(e) => onAuthTabKeyDown(e, i)}
              >
                {t === 'login' ? 'Returning user' : 'New to Navigator'}
              </button>
            ))}
          </div>

          <div key={tab} id="auth-panel" role="tabpanel" aria-labelledby={`auth-tab-${tab}`} tabIndex={0} className="tab-panel">

          {tab === 'login' ? (
            <>
              <h1>Welcome back</h1>
              <p className="auth-sub">Sign in to your Navigator workspace.</p>
            </>
          ) : (
            <>
              <h1>Create your workspace</h1>
              <p className="auth-sub">
                {workspaceKind === 'INDIVIDUAL'
                  ? 'One account, one personal funding workspace. Find scholarships and grants matched to you.'
                  : 'One account, one organization workspace. Add your team after signup — each teammate creates their own account first.'}
              </p>
            </>
          )}

          {serverError && <Alert tone="error">{serverError}</Alert>}

          {tab === 'login' ? (
            <form onSubmit={submitLogin} noValidate>
              <Field label="Email address" required error={errors.email}>
                <Input id="auth-email" type="email" autoComplete="email" placeholder="you@organization.org"
                  value={email} onChange={(e) => setEmail(e.target.value)} aria-invalid={!!errors.email} />
              </Field>
              <Field label="Password" required error={errors.password}>
                <Input id="auth-password" type="password" autoComplete="current-password" placeholder="Enter your password"
                  value={password} onChange={(e) => setPassword(e.target.value)} aria-invalid={!!errors.password} />
              </Field>
              <p className="text-sm" style={{ marginTop: 'calc(var(--s2) * -1)', marginBottom: 'var(--s4)' }}>
                <button
                  type="button"
                  className="link-more"
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                  onClick={() => setResetNoteOpen(true)}
                >
                  Forgot password?
                </button>
              </p>
              <Button type="submit" variant="primary" size="lg" block disabled={busy}>
                {busy ? 'Signing in…' : 'Sign in →'}
              </Button>
            </form>
          ) : (
            <form onSubmit={submitSignup} noValidate>
              <Field label="Full name" required error={errors.name}>
                <Input id="su-name" type="text" autoComplete="name" placeholder="Jordan Ellis"
                  value={name} onChange={(e) => setName(e.target.value)} aria-invalid={!!errors.name} />
              </Field>

              <div className="field">
                <span className="field-label" id="su-kind-label">
                  What are you signing up for?<span className="required-mark" aria-hidden="true"> *</span>
                </span>
                <div className="choice-cards" role="radiogroup" aria-labelledby="su-kind-label">
                  <button
                    type="button" role="radio" aria-checked={workspaceKind === 'ORGANIZATION'}
                    className={`choice-card${workspaceKind === 'ORGANIZATION' ? ' selected' : ''}`}
                    onClick={() => setWorkspaceKind('ORGANIZATION')}
                  >
                    <strong>For my organization</strong>{' '}
                    <span>Grants &amp; RFPs for nonprofits and businesses</span>
                  </button>
                  <button
                    type="button" role="radio" aria-checked={workspaceKind === 'INDIVIDUAL'}
                    className={`choice-card${workspaceKind === 'INDIVIDUAL' ? ' selected' : ''}`}
                    onClick={() => setWorkspaceKind('INDIVIDUAL')}
                  >
                    <strong>For myself or my family</strong>{' '}
                    <span>Scholarships &amp; personal grants</span>
                  </button>
                </div>
              </div>

              {workspaceKind === 'ORGANIZATION' ? (
                <Field label="Organization" required error={errors.orgName}>
                  <Input id="su-org" type="text" autoComplete="organization" placeholder="Northstar Community Services"
                    value={orgName} onChange={(e) => setOrgName(e.target.value)} aria-invalid={!!errors.orgName} />
                </Field>
              ) : (
                <div className="field">
                  <span className="field-label" id="su-path-label">
                    Who is the funding search for?<span className="required-mark" aria-hidden="true"> *</span>
                  </span>
                  <div className="choice-cards four" role="radiogroup" aria-labelledby="su-path-label">
                    {INDIVIDUAL_PATHS.map((p) => (
                      <button
                        key={p.value} type="button" role="radio" aria-checked={individualPath === p.value}
                        className={`choice-card${individualPath === p.value ? ' selected' : ''}`}
                        onClick={() => setIndividualPath(p.value)}
                      >
                        <strong>{p.title}</strong>{' '}
                        <span>{p.desc}</span>
                      </button>
                    ))}
                  </div>
                  {errors.individualPath && <div className="field-error" role="alert">{errors.individualPath}</div>}
                </div>
              )}

              <Field label={workspaceKind === 'INDIVIDUAL' ? 'Email address' : 'Work email'} required error={errors.suEmail}>
                <Input id="su-email" type="email" autoComplete="email" placeholder="you@example.com"
                  value={suEmail} onChange={(e) => setSuEmail(e.target.value)} aria-invalid={!!errors.suEmail}
                  onBlur={() => {
                    // Inline format check before submit.
                    setErrors((prev) => {
                      const invalid = suEmail.trim() !== '' && !EMAIL_RE.test(suEmail.trim());
                      if (invalid) return { ...prev, suEmail: 'Enter a valid email address.' };
                      if (prev.suEmail === 'Enter a valid email address.') {
                        const next = { ...prev };
                        delete next.suEmail;
                        return next;
                      }
                      return prev;
                    });
                  }} />
              </Field>
              <Field label="Password" required hint="At least 8 characters." error={errors.suPassword}>
                <Input id="su-password" type="password" autoComplete="new-password" placeholder="Create a password"
                  value={suPassword} onChange={(e) => setSuPassword(e.target.value)} aria-invalid={!!errors.suPassword} />
              </Field>
              <Button type="submit" variant="primary" size="lg" block disabled={busy}>
                {busy ? 'Creating…' : workspaceKind === 'INDIVIDUAL' ? 'Start my funding search →' : 'Create my workspace →'}
              </Button>
              <p className="text-sm text-muted" style={{ marginTop: 'var(--s3)' }}>
                Navigator accounts are for adults (18+). Signing up for your child or
                student? Choose that option above — you’ll manage their profile from
                your account; they don’t get their own account.
              </p>
            </form>
          )}

          <div className="divider">or continue with</div>
          {/* R2-BR-D33: ONE stable control whose rendering is a pure
              function of the resolved OAuth status. While the status
              is still loading (oauthReady === null) the control says
              so — it must not impersonate either final state (an
              enabled-looking link, or the disabled "being configured"
              button) the way the old tri-state flip did. */}
          {oauthReady === null ? (
            <button type="button" className="oauth-btn" disabled aria-busy="true">
              <GoogleMark /> Checking Google sign-in…
            </button>
          ) : googleEnabled ? (
            <a
              className="oauth-btn"
              href={googleHref}
              onClick={handleGoogleClick}
            >
              <GoogleMark /> Continue with Google
            </a>
          ) : (
            <button type="button" className="oauth-btn" disabled title="Google sign-in is being configured">
              <GoogleMark /> Continue with Google
            </button>
          )}
          {!googleEnabled && oauthReady === false && (
            <p className="text-sm text-muted" style={{ marginTop: 'var(--s2)', textAlign: 'center' }}>
              Google sign-in is being configured for this workspace.
            </p>
          )}

          </div>

          <p className="text-sm text-muted" style={{ marginTop: 'var(--s5)', textAlign: 'center' }}>
            <Link to="/">← Back to homepage</Link>
          </p>
        </div>
      </main>

      {resetNoteOpen && (
        <ForgotPasswordModal initialEmail={email} onClose={() => setResetNoteOpen(false)} />
      )}
    </div>
  );
}
